import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, HelpCircle } from "lucide-react";

const API = "/api";

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => (
  <div
    className={`border-b border-gray-100 last:border-b-0 transition-colors ${isOpen ? "bg-pink-50/50" : ""}`}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 px-5 sm:px-6 text-left gap-4 group"
      data-testid={`faq-toggle-${index}`}
    >
      <span className="font-semibold text-gray-900 text-sm sm:text-base group-hover:text-pink-600 transition-colors">
        {question}
      </span>
      <ChevronDown
        className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180 text-pink-500" : ""}`}
      />
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[600px] pb-5" : "max-h-0"}`}
    >
      <p className="px-5 sm:px-6 text-sm text-gray-500 leading-relaxed">{answer}</p>
    </div>
  </div>
);

export const FAQSection = ({ citySlug = "antalya", lang = "ru", t }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [data, setData] = useState([]);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/faq`, { params: { kind: "city", city: citySlug, lang } })
      .then((res) => { if (active) setData(res.data.items || []); })
      .catch(() => { if (active) setData([]); });
    return () => { active = false; };
  }, [citySlug, lang]);

  if (data.length === 0) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="faq-section">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-1.5 rounded-full mb-4">
            <HelpCircle className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">FAQ</span>
          </div>
          <h2
            className="text-2xl sm:text-3xl font-bold text-gray-900"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            {t ? t("faq.title") : "Частые"}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">
              {t ? t("faq.titleAccent") : "вопросы"}
            </span>
          </h2>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {data.map((item, index) => (
            <FAQItem
              key={index}
              index={index}
              question={item.q}
              answer={item.a}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
